<template>
  <div class="page-view">
    <template v-if="request">
      <header class="page-header">
        <h2 class="tit-h2">
          {{ request.customer.companyName}}
        </h2>
        <img
          v-for="(icon, index) in getFilteredIcons(request)"
          :key="index"
          :src="icon.src"
          :alt="icon.alt"
        />
        <div class="btn-group">
          <el-button
            type="primary"
            size="large"
            @click="router.go(-1)"
          >
            <font-awesome-icon icon="fa-solid fa-arrow-left" />
            뒤로가기
          </el-button>
          <el-button
            v-if="isAuthorized"
            size="large"
            type="primary"
            class="btn-edit"
            @click="handleModifyRequest"
          >
            수정
          </el-button>
          <el-button
            v-if="request.isDeletable"
            size="large"
            type="primary"
            class="btn-edit"
            @click="handleDeleteRequest"
          >
            삭제
          </el-button>
          <el-button
            type="primary"
            size="large"
            @click="toCurrentPlanList"
            v-if="request.detail.requestType === 'PLAN'"
          >
            <font-awesome-icon icon="fa-solid fa-table-list" />
            플랜
          </el-button>
          <el-button
            type="primary"
            size="large"
            @click="toCurrentPaymentList"
          >
            <font-awesome-icon icon="fa-solid fa-table-list" />
            결제
          </el-button>
        </div>
      </header>
      <view-detail :data="request"/>
      <section class="section">
        <h3 class="tit-h3">
          히스토리
        </h3>
        <history-list
          :data="request"
          @open-image-viewer="openImageViewer"
        />
      </section>
    </template>
    <common-loader v-else />
  </div>
  <history-image-viewer
    v-if="showImageViewer"
    :url="currentImage"
    @close="closeImageViewer"
  />
</template>

<script lang="ts" setup>
import {ref, defineProps, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { RequestTypeKeys } from "@/types/constants";
import { RequestInfo } from '@/types/Request';
import { useRequestStore } from '@/stores/request';
import { getRequestInfo, removeRequest, modifyRequest } from "@/api";
import { ElMessageBox, ElNotification, ElMessage } from 'element-plus';
import CommonLoader from '@/components/CommonLoader.vue';
import ViewDetail from "@/components/ViewDetail.vue";
import HistoryList from '@/components/HistoryList.vue';
import HistoryImageViewer from "@/components/HistoryImageViewer.vue";
import StatusTag from '@/components/StatusTag.vue';
import IconBeus from '@/assets/images/ico-beus.svg';
import IconBi from '@/assets/images/ico-bi.svg';
import IconPV from '@/assets/images/ico-pv.svg';
import IconJam from '@/assets/images/ico-jam.svg';
import IconService from '@/assets/images/ico-service.svg';
import {useAuthorizedUsers} from "@/composables/useAuthorizedUsers";

const router = useRouter();

const request = ref<RequestInfo | null>(null);
const isLoading = ref<boolean>(false);
const requestStore = useRequestStore();
const { isAuthorized } = useAuthorizedUsers()

const props = defineProps<{
  id: string;
}>();

const fetchData = async (id: string) => {
  isLoading.value = true;
  const params = {
    requestId: id,
  }
  // console.log('params',params );
  try {
    const response = await getRequestInfo(params);
    // console.log('Reqeust Info', response.result);
    if(response.result) {
      request.value = response.result;
      requestStore.setRequestIds(id, null);
      requestStore.setRequestData(response.result);
    }
  } catch (error) {
    console.error('Error Message:', error);
    ElMessageBox.alert('데이터를 가져올 수 없습니다. 대시보드로 이동합니다.', {
      confirmButtonText: '확인',
      callback: () => {
        router.push("/request");
      },
    });
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  fetchData(props.id);
});

const isCopyTooltipVisible = ref(false);
const copyUserId = async(text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    isCopyTooltipVisible.value = true;
    setTimeout(() => {
      isCopyTooltipVisible.value = false;
    }, 1000);
  } catch (error) {
    console.error('텍스트 복사 실패:', error);
    ElMessage.error('아이디 복사를 실패했습니다. 다시 시도해주세요');
  }
}

interface RequestDetailCondition {
  requestType: RequestTypeKeys;
  beusPlanNumber: number | null;
  baPlanNumber: number | null;
}

interface Icon {
  condition: (detail: RequestDetailCondition) => boolean;
  src: string;
  alt: string;
}

const icons: Icon[] = [
  {
    condition: ({ requestType, beusPlanNumber }: RequestDetailCondition) => 
      requestType === 'PLAN' && !!beusPlanNumber,
    src: IconBeus,
    alt: '플랜-히트맵',
  },
  {
    condition: ({ requestType, baPlanNumber }: RequestDetailCondition) => 
      requestType === 'PLAN' && !!baPlanNumber,
    src: IconBi,
    alt: '플랜-저니맵',
  },
  {
    condition: ({ requestType }: RequestDetailCondition) => 
      requestType === 'PV_CHARGE',
    src: IconPV,
    alt: 'PV 충전',
  },
  {
    condition: ({ requestType }: RequestDetailCondition) => 
      requestType === 'JAM_CHARGE',
    src: IconJam,
    alt: 'JAM 충전',
  },
  {
    condition: ({ requestType }: RequestDetailCondition) => 
      requestType === 'ADDITIONAL',
    src: IconService,
    alt: '부가서비스',
  },
];

const getFilteredIcons = (request: RequestInfo): Icon[] => {
  const detail = {
    requestType: request.detail.requestType,
    baPlanNumber: request.detail.baPlanNumber,
    beusPlanNumber: request.detail.beusPlanNumber
  }
  return icons.filter((icon) => icon.condition(detail));
};

const requiredLogTypes = [
  'CREATED_EAGLE_PLAN',
  'CREATED_EAGLE_PV',
  'CREATED_EAGLE_JAM',
  'PROCESSED_REQUEST_ADDITIONAL',
];

const isModifiable = computed(() => {
  const hasRequiredLog = requiredLogTypes.some((logType) => {
    const exists = request.value?.history.some((item) => item.logType === logType);
    return exists;
  });

  if (!hasRequiredLog) {
    return true;
  }

  const isBeforeToday = request.value ? new Date(request.value.detail.useStartDate) > new Date() : false;

  if(request.value?.history.some((item) => item.logType === 'CREATED_EAGLE_PLAN')) return isBeforeToday;
  else return false;
});

const handleModifyRequest = () => router.push(`/request/modify/${props.id}`);
// const handleModifyRequest = () => router.push({ name: 'Modify', query: {
//   request: request.value
// }});

const isDeletable = computed(() => {
  const hasRequiredLog = requiredLogTypes.some((logType) => {
    const exists = request.value?.history.some((item) => item.logType === logType);
    return exists;
  });

  if (!hasRequiredLog) {
    return true;
  }

  const isBeforeToday = request.value ? new Date(request.value.detail.useStartDate) > new Date() : false;
  return isBeforeToday;
});

const handleDeleteRequest = async () => {
  try {
    await ElMessageBox.confirm(
      '리퀘스트를 정말 삭제하시겠습니까?<br/>삭제한 리퀘스트는 복구할 수 없습니다.',
      '경고',
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: '확인',
        cancelButtonText: '취소',
      }
    );
    
    const params = { requestId: props.id }
    const response = await removeRequest(params);

    if(response.success) {
      ElMessage({
        message: '삭제가 완료되었습니다.',
        type: 'success',
      });
      router.push({name: 'Board'});
    }

  } catch (error) {
    if (error === 'cancel') {
      return;
    }
    console.error('Error messege:', error);
    ElMessage({
      message: '삭제 중 오류가 발생했습니다.',
      type: 'error',
    });
  }
}

const showImageViewer = ref(false);
const currentImage = ref('');
const openImageViewer = (url = '') => {
  currentImage.value = url;
  showImageViewer.value = true;
};
const closeImageViewer = () => {
  showImageViewer.value = false;
};

const toCurrentPlanList = () => router.push({ path: '/request/plan', query: { param: request.value?.customer.customerId } });
const toCurrentPaymentList = () => router.push({ path: '/request/payment', query: { param: request.value?.customer.customerId } });
</script>